import { AppPages } from "utils/enums/app-pages";
import {
  getPagePermissions,
  hideLoaderForBatchApiCall,
  printPDFHelper,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  ISLPViewContainerDispatch,
  IUpdateSLPParticipationValue,
} from "utils/interfaces/slp";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getUserSelector } from "store/auth/selector";
import { AuthState as AuthType } from "store/auth/types";
import { AuthState } from "store/rootReducer";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import {
  ICompletedSLPDetails,
  ISLPFormsView,
} from "utils/interfaces/student-profile-details";
import { CompletedSLPDetailsSuccessPayload } from "store/studentProfileDetails/types";
import moment from "moment";
import { DATE_VIEW_FORMAT } from "utils/constants/constant";
import { ViewSLPGoalsAssessmentPDFSuccessPayload } from "store/slpDashboard/type";
import { SLPFormStep } from "utils/enums/slp-form";
import { SLPParticipation } from "utils/enums/slp";
import {
  getSLPParticipationClass,
  getSLPParticipationText,
} from "utils/helper/slp";
import { editGreyIcon, editPurpleIcon } from "assets/images";
import { SLP_PARTICIPATION_UPDATE_MSG } from "utils/constants/Messages";
import { toast } from "react-toastify";
import SLPParticipationModal from "components/SLPParticipationModal";
import SLPGoalsAndAssessment from "./SLPGoalsAndAssessment";

export type SLPViewProps = ISLPViewContainerDispatch;

interface IStudentDetailBlock {
  title: string;
  items: { label: string; value: string | number | undefined }[];
  participation?: SLPParticipation;
  handleParticipationClick: () => void;
}

const StudentDetailBlock = ({
  title,
  items,
  participation = SLPParticipation.Active,
  handleParticipationClick,
}: IStudentDetailBlock) => (
  <Grid item xs={12}>
    <Card className="info-card">
      <Box className="card-inner-head mb-24">
        <Typography variant="h3" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
          }}
        >
          <Typography variant="body1" sx={{ color: "#9E9E9E" }}>
            SLP Participation
          </Typography>
          <Button
            className={`btn-progress ${getSLPParticipationClass(
              participation
            )}`}
            variant="outlined"
            onClick={handleParticipationClick}
          >
            <img
              src={
                participation === SLPParticipation.Active
                  ? editPurpleIcon
                  : editGreyIcon
              }
              alt="Edit Participation"
              className="default"
            />
            <Typography variant="body2">
              {getSLPParticipationText(participation)}
            </Typography>
          </Button>
        </Box>
      </Box>
      <Grid container spacing={{ xs: "16px", lg: "20px", xl: "24px" }}>
        {items.map(({ label, value }) => (
          <Grid item xs={12} sm={6} md={4} xl={3} key={`${label}-${value}`}>
            <Typography variant="body1" className="sco-detail-title">
              {label}
            </Typography>
            <Typography variant="body1" className="sco-detail">
              {value ?? "-"}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Card>
  </Grid>
);

interface IContactNoteBlock {
  title: string;
  text: string | undefined;
}
const ContactNoteBlock = ({ title, text }: IContactNoteBlock) => (
  <Grid item xs={12}>
    <Card className="info-card">
      <Box className="card-inner-head mb-24">
        <Typography variant="h3" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      <Grid container spacing={{ xs: "16px", lg: "20px", xl: "24px" }}>
        <Grid item xs={12}>
          <Typography variant="body1" className="sco-detail">
            {text ?? "-"}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  </Grid>
);

const renderSLPForm = (forms: ISLPFormsView[]) =>
  forms.map((record) => (
    <Grid item xs={12} key={record.slpFormId}>
      <Typography variant="body1" className="sco-detail-title">
        {record.question}
      </Typography>
      <Typography variant="body1" className="sco-detail">
        {record.answer ?? "-"}
      </Typography>
    </Grid>
  ));

const SLPView: React.FC<SLPViewProps> = (props) => {
  const user = useSelector<AuthState, AuthType["user"]>(getUserSelector);
  const history = useHistory();
  const location = useLocation<{ studentId: number; slpId: number }>();
  const { studentId, slpId } = location.state;
  const {
    getCompletedSLPDetailsRequest,
    getAllSLPGoalsAndAssessmentRequest,
    viewSLPGoalsAssessmentPDFRequest,
    deletePersonalAndSmartGoalsRequest,
    addPersonalAndSmartGoalsRequest,
    updateSLPParticipationRequest,
  } = props;

  const [slpDetails, setSLPDetails] = useState<ICompletedSLPDetails | null>(
    null
  );
  const [slpParticipationModalDetail, setSLPParticipationModalDetail] =
    useState<IUpdateSLPParticipationValue>();

  const getCompletedSLPDetails = () => {
    if (getCompletedSLPDetailsRequest && slpId) {
      showLoaderForBatchApiCall();
      getCompletedSLPDetailsRequest({
        values: { id: slpId },
        callback: (response: CompletedSLPDetailsSuccessPayload) => {
          hideLoaderForBatchApiCall();
          setSLPDetails(response.completedSLPDetails);
        },
      });
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    const isViewAllowed = getPagePermissions(
      user?.permissions,
      AppPages.SLPDashboard,
      "student learning plan"
    )?.view;

    if (!isViewAllowed) {
      history.goBack();

      return;
    }

    getCompletedSLPDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSLPGoalsAssessmentPDF = useCallback(() => {
    if (viewSLPGoalsAssessmentPDFRequest && studentId && slpId) {
      showLoaderForBatchApiCall();
      viewSLPGoalsAssessmentPDFRequest({
        value: { studentId, slpId },
        callback: (response: ViewSLPGoalsAssessmentPDFSuccessPayload) => {
          hideLoaderForBatchApiCall();
          printPDFHelper(response.goalsAssessmentPDF);
        },
      });
    }
  }, [slpId, studentId, viewSLPGoalsAssessmentPDFRequest]);

  if (!slpDetails) return null;

  const handleParticipationModalClose = () => {
    setSLPParticipationModalDetail(undefined);
  };

  const updateParticipationCallback = () => {
    toast.success(SLP_PARTICIPATION_UPDATE_MSG);
    getCompletedSLPDetails();
    handleParticipationModalClose();
  };

  const handleParticipationClick = () => {
    setSLPParticipationModalDetail({
      studentId,
      slpParticipation: slpDetails.slpParticipation ?? SLPParticipation.Active,
    });
  };

  return (
    <>
      <Box className="content-header simple-header">
        <Typography variant="h2" className="heading">
          View SLP Goals & Assessment
        </Typography>
        <Box sx={{ display: "flex", columnGap: "14px" }}>
          <Button
            variant="outlined"
            className="px-16"
            onClick={getSLPGoalsAssessmentPDF}
            aria-label="Print SLP Goals & Assessment"
          >
            Print
          </Button>
          <Button
            variant="outlined"
            className="px-16"
            onClick={() => history.goBack()}
            aria-label="Go back"
          >
            Back
          </Button>
        </Box>
      </Box>

      <Grid
        container
        spacing={{ xs: "16px", lg: "20px", xl: "24px" }}
        className="content-container"
      >
        <StudentDetailBlock
          title="Student Details"
          items={[
            { label: "Full Name", value: slpDetails.studentName },
            { label: "SLP number", value: slpDetails.slpNumber },
            {
              label: "Date Completed",
              value: slpDetails.dateCompleted
                ? moment(slpDetails.dateCompleted).format(DATE_VIEW_FORMAT)
                : "-",
            },
            { label: "PLA Name", value: slpDetails.allocatedToName },
            {
              label: "Active Community eMR (at enrolment)",
              value: slpDetails.activeCommunityEMR,
            },
          ]}
          handleParticipationClick={handleParticipationClick}
          participation={slpDetails.slpParticipation}
        />

        <ContactNoteBlock
          title="Contact Notes"
          text={slpDetails.contactNotes}
        />

        <Grid item xs={12}>
          <Card className="info-card">
            <Box className="card-inner-head mb-24">
              <Typography variant="h3" sx={{ fontWeight: 500 }}>
                Hopes, Goals and Ambitions
              </Typography>
            </Box>
            <Grid container spacing={{ xs: "16px", lg: "20px", xl: "24px" }}>
              {renderSLPForm(
                slpDetails.slpForms.filter(
                  (x) => x.partNumber === SLPFormStep.HOPES_GOALS_AMBITIONS
                )
              )}
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card className="info-card">
            <Box className="card-inner-head mb-24">
              <Typography variant="h3" sx={{ fontWeight: 500 }}>
                Where to from Here
              </Typography>
            </Box>
            <Grid container spacing={{ xs: "16px", lg: "20px", xl: "24px" }}>
              {renderSLPForm(
                slpDetails.slpForms.filter(
                  (x) => x.partNumber === SLPFormStep.WHERE_TO_FROM_HERE
                )
              )}
            </Grid>
          </Card>
        </Grid>

        {studentId && (
          <SLPGoalsAndAssessment
            nextOrCurrentSLPId={slpDetails.nextOrCurrentSLPId ?? 0}
            getAllSLPGoalsAndAssessmentRequest={
              getAllSLPGoalsAndAssessmentRequest
            }
            studentId={studentId}
            studentName={slpDetails.studentName}
            deletePersonalAndSmartGoalsRequest={
              deletePersonalAndSmartGoalsRequest
            }
            addPersonalAndSmartGoalsRequest={addPersonalAndSmartGoalsRequest}
            slpParticipation={slpDetails.slpParticipation}
          />
        )}

        {!!slpParticipationModalDetail && (
          <SLPParticipationModal
            onClose={handleParticipationModalClose}
            slpParticipationDetail={slpParticipationModalDetail}
            updateSLPParticipationRequest={updateSLPParticipationRequest}
            onSaveCallback={updateParticipationCallback}
          />
        )}
      </Grid>
    </>
  );
};

export default SLPView;
